import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory, Redirect } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import Button from "components/global/Button";
import Spacer from "components/global/Spacer";
import CheckCircle from "icons/CheckCircleIcon";
import { newPlans } from "views/plans/planData";
import {
  getPlans,
  attachPlan,
  getAccount,
  getUser,
  setUserFreeTrial,
} from "api";
import { showAlert } from "utils/utils";
import Loader from "components/global/Loader";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .headerContainer {
    max-width: 400px;
    text-align: center;
    gap: 6px;

    p {
      font-size: 14px;
      line-height: 20px;
    }
  }

  .bodyContainer {
    margin: 0 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .plansGrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-top: 24px;
  }

  .planCard {
    border: 1px solid var(--grey_10);
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    cursor: pointer;
    transition: var(--transition_default);

    &:hover {
      border-color: var(--secondary);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &.selected {
      border: 2px solid var(--secondary);
      background-color: #f7fafd;
    }

    .plan-detail {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .plan-name {
        text-transform: capitalize;
        font-size: 18px;
        font-weight: 700;

        &.Free {
          color: #535362;
        }
        &.Essential {
          color: var(--secondary);
        }
        &.Growth {
          color: #6a50fb;
        }
        &.Agency {
          color: #0f1f7b;
        }
      }

      .plan-price {
        font-size: 32px;
        font-weight: 700;
        line-height: 40px;
      }

      .description-price {
        font-size: 12px;
        font-weight: 450;
        color: var(--body_text);
      }
    }

    .plan-features {
      .title {
        font-size: 12px;
        font-weight: 500;
        color: var(--grey_1);
        margin-bottom: 12px;
      }

      .other-plan-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;

        .list-item {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-size: 14px;
          line-height: 20px;

          span {
            min-width: 16px;
            margin-top: 2px;
          }
        }
      }
    }
  }

  .footer {
    position: sticky;
    bottom: 0;
    background-color: #fff;
    padding-top: 24px;
    margin-top: 24px;
  }

  @media screen and (max-width: 768px) {
    margin-bottom: 100px;

    .headerContainer,
    .bodyContainer {
      max-width: 327px;
    }

    .plansGrid {
      grid-template-columns: 1fr;
    }
  }
`;

const SelectPlan = ({
  allplans,
  onboarding_state,
  active_account,
  user,
  current_sub,
}) => {
  const history = useHistory();
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const { isLoading: isLoadingPlans } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      await getPlans();
      return true; // getPlans updates Redux store directly
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const attachPlanMutation = useMutation({
    mutationFn: (planId) => attachPlan(planId),
    onSuccess: async (res) => {
      if (res?.data?.status === true) {
        try {
          setUserFreeTrial("ongoing");

          Promise.all([getUser(), getAccount()]).catch(console.error);

          showAlert({
            msg: "Plan selected successfully! You have 14 days to try it out.",
            type: "success",
          });

          // Redirect to create brand (next step)
          history.replace("/onboarding/create-brand");
        } catch (error) {
          showAlert({
            msg: "An error occurred while saving plan selection",
            type: "danger",
          });
        }
      } else {
        showAlert({
          msg: res?.data?.message || "Failed to attach plan. Please try again.",
          type: "danger",
        });
      }
    },
    onError: (error) => {
      showAlert({
        msg:
          error?.response?.data?.message ||
          "An error occurred. Please try again.",
        type: "danger",
      });
    },
  });

  const handleSelectPlan = (planId) => {
    setSelectedPlanId(planId);
  };

  const handleContinue = () => {
    if (!selectedPlanId) {
      showAlert({
        msg: "Please select a plan to continue",
        type: "danger",
      });
      return;
    }

    attachPlanMutation.mutate(selectedPlanId);
  };

  if (user?.free_trial && user.free_trial !== "unused") {
    return <Redirect to="/onboarding/create-brand" />;
  }

  if (!user?.free_trial) {
    return <Loader />;
  }

  if (isLoadingPlans) {
    return <Loader />;
  }

  // Filter out free plan (id: 1) from onboarding
  const availablePlans = allplans?.filter((plan) => plan.id !== 1) || [];

  if (availablePlans.length === 0) {
    return (
      <Wrapper>
        <div className="headerContainer">
          <h4>Select a Plan</h4>
          <p className="l3 colorBodyText">
            No plans available at the moment. Please contact support.
          </p>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="headerContainer">
        <h4>Choose your plan</h4>
        <p className="l3 colorBodyText">
          Select a plan to get started. You&apos;ll have 14 days to try it out
          before billing begins.
        </p>
      </div>
      <Spacer y={3} />
      <div className="bodyContainer">
        <div className="plansGrid">
          {availablePlans.map((plan) => {
            const planFeature = newPlans.find((p) => p.id === plan.id);
            const isSelected = selectedPlanId === plan.id;

            return (
              <div
                key={plan.id}
                className={`planCard ${isSelected ? "selected" : ""}`}
                onClick={() => handleSelectPlan(plan.id)}
              >
                <div className="plan-detail">
                  <h4 className={`plan-name ${plan.name}`}>{plan.name}</h4>
                  <div>
                    <h5 className="plan-price">
                      {plan.currency === "NGN" ? "₦" : "$"}
                      {plan.price.replace(/\.00/g, "")}
                    </h5>
                    <Spacer y={0.8} />
                    <p className="description-price">billed monthly</p>
                  </div>
                </div>
                <div className="plan-features">
                  {planFeature?.note && (
                    <p className="title">{planFeature?.note}</p>
                  )}
                  <ul className="other-plan-list">
                    {planFeature?.features?.slice(0, 5).map((feat, idx) => (
                      <li className="list-item description" key={idx}>
                        <span>
                          <CheckCircle
                            size="16px"
                            color={
                              plan.id === 2
                                ? "var(--secondary)"
                                : plan.id === 3
                                ? "#6A50FB"
                                : "#0F1F7B"
                            }
                          />
                        </span>
                        {feat}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
        <div className="footer">
          <Button
            className="submit-btn"
            onClick={handleContinue}
            text={"Continue"}
            bg="var(--secondary)"
            loading={attachPlanMutation.isPending}
            disabled={!selectedPlanId || attachPlanMutation.isPending}
            fullWidth
          />
        </div>
      </div>
    </Wrapper>
  );
};

const mapStateToProps = (state) => {
  return {
    allplans: state.plans,
    onboarding_state: state.guided_tours,
    active_account: state.active_account,
    user: state.user,
    current_sub: state.current_sub,
  };
};

export default connect(mapStateToProps)(SelectPlan);
